/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Twilio credential setup.
 *
 * Follows PaymentGatewaySetupPage, including its masked-secret discipline,
 * which is the important part rather than a stylistic one. Stored secrets are
 * rendered as bullets; a save only sends a secret the user actually retyped.
 * Without that, editing an unrelated field round-trips the mask back as the
 * credential, Twilio starts rejecting every call, and the page still says
 * "Connected".
 *
 * Pearl asks for an API Key rather than only the Auth Token because the voice
 * access tokens the browser softphone needs are signed with an API key. The
 * Auth Token is requested separately and only for validating Twilio's webhook
 * signatures -- it is the highest-blast-radius secret here, which is why the
 * page recommends a subaccount.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack,
  CheckCircle,
  ContentCopy,
  OpenInNew,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "auth/Auth";
import { getApiErrorMessage } from "utils/apiErrors";
import SoftphoneBarSettingsCard from "components/Softphone/SoftphoneBarSettingsCard";
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";
import { ToneChip } from "components/v2/settings/SettingsBlocks";

/**
 * Twilio Regions the REST API can be called in.
 *
 * A Region is an isolated Twilio deployment with its own credentials: an API
 * Key is only valid in the Region it was created in, so a key made under
 * Australia (AU1) fails against US1 with error 70051 even when it is Standard,
 * active and in the right account. This is NOT the country of your phone
 * numbers -- numbers are a global, account-level pool, and a +61 number is
 * served from US1 unless someone deliberately chose otherwise. Almost every
 * tenant wants US1; pick another only for data residency.
 */
const TWILIO_REGIONS = [
  { value: "us1", label: "United States (US1) — default" },
  { value: "au1", label: "Australia (AU1)" },
  { value: "ie1", label: "Ireland (IE1)" },
  { value: "jp1", label: "Japan (JP1)" },
];

// Twilio's own brand colour, not ours; it does not belong in our tokens.
const TWILIO_RED = "#F22F46";
const MASK = "••••••••••••••••";

const CONSOLE_URL = "https://console.twilio.com";

function InstructionCard({ title, children, action }) {
  return (
    <MDBox
      sx={{
        p: 2.5,
        mb: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "#FAFBFC",
      }}
    >
      <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
        {title}
      </MDTypography>
      <MDTypography variant="body2" color="text" sx={{ lineHeight: 1.7 }}>
        {children}
      </MDTypography>
      {action && <MDBox mt={2}>{action}</MDBox>}
    </MDBox>
  );
}

function NumberedSteps({ steps }) {
  return (
    <MDBox
      component="ol"
      sx={{
        m: 0,
        pl: 2.5,
        "& li": { mb: 1.25, lineHeight: 1.65, color: "text.secondary" },
        "& li::marker": { color: TWILIO_RED, fontWeight: 700 },
      }}
    >
      {steps.map((step) => (
        <MDTypography key={step} component="li" variant="body2" color="text">
          {step}
        </MDTypography>
      ))}
    </MDBox>
  );
}

function CopyField({ label, value, helper }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard blocked; the value is selectable in the field regardless */
    }
  };
  return (
    <TextField
      fullWidth
      label={label}
      value={value || ""}
      helperText={helper}
      margin="normal"
      InputProps={{
        readOnly: true,
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title={copied ? "Copied" : "Copy"}>
              <span>
                <IconButton onClick={copy} edge="end" disabled={!value}>
                  {copied ? <CheckCircle color="success" /> : <ContentCopy />}
                </IconButton>
              </span>
            </Tooltip>
          </InputAdornment>
        ),
      }}
    />
  );
}

export default function TwilioSetupPage() {
  const isV2 = useTheme()?.pearlUi === "v2";
  const navigate = useNavigate();
  const { axiosInstance } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [testResult, setTestResult] = useState(null);

  const [settings, setSettings] = useState(null);
  const [accountSid, setAccountSid] = useState("");
  const [apiKeySid, setApiKeySid] = useState("");
  const [twimlAppSid, setTwimlAppSid] = useState("");
  const [defaultRegion, setDefaultRegion] = useState("AE");
  // The backend stores the US1 default as null; "us1" is this control's way of
  // saying the same thing, so that the select is never blank.
  const [twilioRegion, setTwilioRegion] = useState("us1");
  const [enabled, setEnabled] = useState(false);

  // Secret fields: value + "did the user actually retype it" + reveal toggle.
  const [apiSecret, setApiSecret] = useState("");
  const [apiSecretEdited, setApiSecretEdited] = useState(false);
  const [showApiSecret, setShowApiSecret] = useState(false);
  const [authToken, setAuthToken] = useState("");
  const [authTokenEdited, setAuthTokenEdited] = useState(false);
  const [showAuthToken, setShowAuthToken] = useState(false);

  const apply = useCallback((data) => {
    setSettings(data);
    setAccountSid(data.twilio_account_sid || "");
    setApiKeySid(data.twilio_api_key_sid || "");
    setTwimlAppSid(data.twilio_twiml_app_sid || "");
    setDefaultRegion(data.default_region || "AE");
    setTwilioRegion(data.twilio_region || "us1");
    setEnabled(Boolean(data.enabled));
    // Show stored secrets as bullets so the field never looks empty when one
    // is set -- an empty field reads as "not configured" and invites a retype.
    setApiSecret(data.twilio_api_key_secret_masked || (data.has_api_key_secret ? MASK : ""));
    setAuthToken(data.twilio_auth_token_masked || (data.has_auth_token ? MASK : ""));
    setApiSecretEdited(false);
    setAuthTokenEdited(false);
    setShowApiSecret(false);
    setShowAuthToken(false);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get("/telephony/settings");
      apply(res.data);
    } catch (err) {
      setError(getApiErrorMessage(err, "Unable to load telephony settings."));
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, apply]);

  useEffect(() => {
    load();
  }, [load]);

  /** Clear the mask on first focus so the user types into an empty field. */
  const beginEditing = (edited, setEdited, setValue, setShow) => () => {
    if (!edited) {
      setEdited(true);
      setValue("");
      setShow(true);
    }
  };

  const secretPayload = (edited, value) =>
    edited && value.trim() && !String(value).includes("•") ? value.trim() : undefined;

  const save = async (overrides = {}) => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        twilio_account_sid: accountSid.trim() || null,
        twilio_api_key_sid: apiKeySid.trim() || null,
        twilio_twiml_app_sid: twimlAppSid.trim() || null,
        default_region: (defaultRegion || "AE").toUpperCase(),
        twilio_region: twilioRegion === "us1" ? null : twilioRegion,
        ...overrides,
      };

      const nextApiSecret = secretPayload(apiSecretEdited, apiSecret);
      if (nextApiSecret) payload.twilio_api_key_secret = nextApiSecret;

      const nextAuthToken = secretPayload(authTokenEdited, authToken);
      if (nextAuthToken) payload.twilio_auth_token = nextAuthToken;

      const res = await axiosInstance.put("/telephony/settings", payload);
      apply(res.data);
      setToast("Twilio settings saved.");
      return res.data;
    } catch (err) {
      setError(getApiErrorMessage(err, "Unable to save telephony settings."));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (next) => {
    // Optimistic, then reconciled from the response: the server refuses to
    // enable a half-configured account, and that refusal must win.
    setEnabled(next);
    const saved = await save({ enabled: next });
    if (!saved) setEnabled(!next);
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    setError("");
    try {
      const res = await axiosInstance.post("/telephony/settings/test-connection");
      setTestResult(res.data);
    } catch (err) {
      setTestResult({
        ok: false,
        message: getApiErrorMessage(err, "Connection test failed."),
        numbers: [],
      });
    } finally {
      setTesting(false);
    }
  };

  const configured = Boolean(settings?.is_configured);
  const encryptionAvailable = settings?.encryption_available !== false;

  const completedSteps = useMemo(() => {
    let n = 0;
    if (accountSid.trim()) n += 1;
    if (apiKeySid.trim() && (apiSecret.trim() || settings?.has_api_key_secret)) n += 1;
    if (twimlAppSid.trim()) n += 1;
    return n;
  }, [accountSid, apiKeySid, apiSecret, twimlAppSid, settings]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={isV2 ? 0 : 4} pb={6} px={isV2 ? 0 : 2}>
        {isV2 ? (
          <PageHeader
            title="Twilio"
            summary="Connect your own Twilio account so Pearl can place and receive calls on your numbers"
            actions={
              configured ? (
                <ToneChip tone={enabled ? "success" : "warning"}>{enabled ? "Connected" : "Configured · Off"}</ToneChip>
              ) : (
                <ToneChip>{`Step ${completedSteps + 1} of 4`}</ToneChip>
              )
            }
          />
        ) : (
          <MDBox display="flex" alignItems="center" gap={1} mb={3}>
            <IconButton onClick={() => navigate("/settings/telephony")}>
              <ArrowBack />
            </IconButton>
            <MDBox flexGrow={1}>
              <MDBox display="flex" alignItems="center" gap={1.5}>
                <MDTypography variant="h4" fontWeight="medium">
                  Twilio
                </MDTypography>
                {configured ? (
                  <Chip
                    icon={<CheckCircle />}
                    label={enabled ? "Connected" : "Configured · Disabled"}
                    color={enabled ? "success" : "warning"}
                    size="small"
                  />
                ) : (
                  <Chip label={`Step ${completedSteps + 1} of 4`} size="small" variant="outlined" />
                )}
              </MDBox>
              <MDTypography variant="body2" color="text">
                Connect your own Twilio account so Pearl can place and receive calls on your numbers
              </MDTypography>
            </MDBox>
          </MDBox>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {/* getApiErrorMessage handles the object shapes the API actually
                returns (FastAPI detail arrays, the RBAC 403 payload). The old
                JSON.stringify fallback rendered a raw object dump at the user
                the one time it fired. */}
            {typeof error === "string" ? error : getApiErrorMessage(error, "Something went wrong.")}
          </Alert>
        )}

        {!encryptionAvailable && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Credential encryption is not configured on this server, so secrets cannot be saved.
            Ask your administrator to set <code>SECRET_ENCRYPTION_KEYS</code> and restart the API.
          </Alert>
        )}

        {loading ? (
          <MDBox display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </MDBox>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} lg={7}>
              <Card sx={{ p: 3, border: "1px solid #E6E8F0", boxShadow: "none", borderRadius: "12px" }}>
                <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
                  1 · Twilio account
                </MDTypography>
                <MDTypography variant="body2" color="text" mb={2}>
                  Found on the Twilio console home page.
                </MDTypography>
                <TextField
                  fullWidth
                  label="Account SID"
                  placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  value={accountSid}
                  onChange={(e) => setAccountSid(e.target.value)}
                  margin="normal"
                  helperText="Not a secret — identifies which Twilio account Pearl talks to."
                />

                <Divider sx={{ my: 3 }} />

                <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
                  2 · API key
                </MDTypography>
                <MDTypography variant="body2" color="text" mb={2}>
                  Pearl signs the browser softphone's access tokens with this. Create one under
                  Account → API keys & tokens → Create API key (Standard). Set the Region
                  selector on that page <em>before</em> creating the key — the Region cannot be
                  changed afterwards, and a key from the wrong one is rejected.
                </MDTypography>
                <TextField
                  fullWidth
                  label="API Key SID"
                  placeholder="SKxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  value={apiKeySid}
                  onChange={(e) => setApiKeySid(e.target.value)}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="API Key Secret"
                  type={showApiSecret ? "text" : "password"}
                  value={apiSecret}
                  onChange={(e) => setApiSecret(e.target.value)}
                  onFocus={beginEditing(
                    apiSecretEdited, setApiSecretEdited, setApiSecret, setShowApiSecret
                  )}
                  margin="normal"
                  helperText={
                    settings?.has_api_key_secret && !apiSecretEdited
                      ? "A secret is stored. Click to replace it; leave it alone to keep it."
                      : "Twilio shows this once, when the key is created."
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          // Revealing a mask would show bullets; only offer it
                          // once there is a real typed value to reveal.
                          disabled={!apiSecretEdited && Boolean(settings?.has_api_key_secret)}
                          onClick={() => setShowApiSecret((v) => !v)}
                        >
                          {showApiSecret ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  select
                  fullWidth
                  label="Twilio Region"
                  value={twilioRegion}
                  onChange={(e) => setTwilioRegion(e.target.value)}
                  margin="normal"
                  helperText={
                    twilioRegion === "us1"
                      ? "Must match the Region the API key was created in — shown on the key's page in the Twilio console. Not the country of your numbers."
                      : "Your API key must have been created with the console's Region selector set to this same Region, or Twilio rejects it with error 70051."
                  }
                >
                  {TWILIO_REGIONS.map((r) => (
                    <MenuItem key={r.value} value={r.value}>
                      {r.label}
                    </MenuItem>
                  ))}
                </TextField>

                <Divider sx={{ my: 3 }} />

                <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
                  3 · TwiML App
                </MDTypography>
                <MDTypography variant="body2" color="text" mb={2}>
                  Outbound calls from the browser route through a TwiML App. Create one under
                  Voice → TwiML → TwiML Apps and paste the <strong>outbound</strong> URL below
                  into its Voice Request URL.
                </MDTypography>
                <TextField
                  fullWidth
                  label="TwiML App SID"
                  placeholder="APxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  value={twimlAppSid}
                  onChange={(e) => setTwimlAppSid(e.target.value)}
                  margin="normal"
                />
                <CopyField
                  label="TwiML App → Voice Request URL"
                  value={settings?.outbound_webhook_url}
                  helper="For calls your agents place. Twilio signs the exact URL it calls, so use this one verbatim."
                />
                <CopyField
                  label="Phone number → A call comes in"
                  value={settings?.voice_webhook_url}
                  helper="For calls arriving from the outside world. Set this on each number under Phone Numbers, NOT on the TwiML App."
                />
                <Alert severity="info" sx={{ mt: 2 }}>
                  These two are not interchangeable. Putting the inbound URL on the TwiML App
                  makes an agent&apos;s outgoing call look like an incoming one, so it is parked
                  in a queue on hold music and the person you dialled is never called — while the
                  console still shows &quot;Connected&quot;.
                </Alert>

                <Divider sx={{ my: 3 }} />

                <MDTypography variant="h6" fontWeight="medium" mb={0.5}>
                  4 · Webhook signature (recommended)
                </MDTypography>
                <MDTypography variant="body2" color="text" mb={2}>
                  Twilio signs its webhooks with your Auth Token. Storing it lets Pearl verify that
                  an inbound call really came from Twilio. Use a{" "}
                  <strong>subaccount</strong> Auth Token so a leak here cannot reach your main
                  account.
                </MDTypography>
                <TextField
                  fullWidth
                  label="Auth Token"
                  type={showAuthToken ? "text" : "password"}
                  value={authToken}
                  onChange={(e) => setAuthToken(e.target.value)}
                  onFocus={beginEditing(
                    authTokenEdited, setAuthTokenEdited, setAuthToken, setShowAuthToken
                  )}
                  margin="normal"
                  helperText={
                    settings?.has_auth_token && !authTokenEdited
                      ? "A token is stored. Click to replace it; leave it alone to keep it."
                      : "Optional, but without it Pearl cannot verify webhook authenticity."
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          disabled={!authTokenEdited && Boolean(settings?.has_auth_token)}
                          onClick={() => setShowAuthToken((v) => !v)}
                        >
                          {showAuthToken ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Default region"
                  value={defaultRegion}
                  onChange={(e) => setDefaultRegion(e.target.value.toUpperCase().slice(0, 2))}
                  margin="normal"
                  inputProps={{ maxLength: 2 }}
                  helperText="Two-letter country code used to read locally-formatted numbers (AE, AU, IN...). Unrelated to the Twilio Region above."
                />

                <MDBox mt={4} display="flex" justifyContent="flex-end" gap={2}>
                  <MDButton
                    variant="outlined"
                    color="info"
                    onClick={runTest}
                    disabled={testing || saving || !configured}
                  >
                    {testing ? <CircularProgress size={18} color="inherit" /> : "Test connection"}
                  </MDButton>
                  <MDButton
                    variant="gradient"
                    color="info"
                    onClick={() => save()}
                    disabled={saving || !encryptionAvailable}
                  >
                    {saving ? <CircularProgress size={18} color="inherit" /> : "Save"}
                  </MDButton>
                </MDBox>
              </Card>
            </Grid>

            <Grid item xs={12} lg={5}>
              <Card sx={{ p: 3, border: "1px solid #E6E8F0", boxShadow: "none", borderRadius: "12px", mb: 3 }}>
                <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <MDTypography variant="h6" fontWeight="medium">
                    Enable telephony
                  </MDTypography>
                  <Switch
                    checked={enabled}
                    disabled={!configured || saving}
                    onChange={(e) => toggleEnabled(e.target.checked)}
                  />
                </MDBox>
                <MDTypography variant="body2" color="text">
                  {configured
                    ? "Turning this on lets agents place and receive calls in Pearl."
                    : "Complete steps 1 to 3 first — Pearl cannot place a call without them."}
                </MDTypography>
              </Card>

              <SoftphoneBarSettingsCard variant={isV2 ? "v2" : "classic"} />

              {testResult && (
                <Alert
                  severity={testResult.ok ? "success" : "error"}
                  sx={{ mb: 3 }}
                  onClose={() => setTestResult(null)}
                >
                  <MDTypography variant="button" fontWeight="bold" display="block">
                    {testResult.message}
                  </MDTypography>
                  {testResult.ok && (
                    <MDTypography variant="caption" color="text">
                      {testResult.numbers?.length
                        ? `${testResult.numbers.length} number(s) found on this account.`
                        : "No phone numbers on this account yet."}
                    </MDTypography>
                  )}
                </Alert>
              )}

              <InstructionCard
                title="Where to find these in Twilio"
                action={
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    href={CONSOLE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    endIcon={<OpenInNew />}
                  >
                    Open Twilio console
                  </MDButton>
                }
              >
                <NumberedSteps
                  steps={[
                    "Account SID is on the console home page, under Account Info.",
                    "Create an API key under Account → API keys & tokens. Copy the secret immediately — Twilio never shows it again.",
                    "Create a TwiML App under Voice → TwiML → TwiML Apps, and paste the Voice Request URL from this page into it.",
                    "Auth Token is on the console home page, next to the Account SID.",
                  ]}
                />
              </InstructionCard>

              <InstructionCard title="Why a subaccount is recommended">
                The Auth Token can do anything your Twilio account can, and Pearl has to store it to
                verify webhook signatures. Creating a subaccount for Pearl keeps that blast radius
                contained: it has its own Auth Token, its own numbers and its own billing line.
              </InstructionCard>

              {settings?.status_webhook_url && (
                <InstructionCard title="Status callback URL">
                  <CopyField label="Status callback" value={settings.status_webhook_url} />
                </InstructionCard>
              )}
            </Grid>
          </Grid>
        )}
      </MDBox>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success" onClose={() => setToast("")} sx={{ width: "100%" }}>
          {toast}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
